import { FileText, Paperclip, X } from 'lucide-react'
import type { ChatAttachment } from '../state/types'
import { AttachmentImage } from './AttachmentImage'

interface AttachmentListProps {
  attachments: ChatAttachment[]
  onRemove: (id: string) => void
}

export function AttachmentList({ attachments, onRemove }: AttachmentListProps) {
  if (attachments.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2 border-b-2 border-nb-border px-3 py-3">
      {attachments.map((attachment) => {
        const isImage = attachment.type.startsWith('image/')
        const progress = attachment.uploadProgress ?? 0

        if (isImage) {
          return (
            <div key={attachment.id} className="relative h-16 w-16 overflow-hidden rounded-base border-2 border-nb-border bg-bg">
              <AttachmentImage
                src={attachment.url}
                mimeType={attachment.type}
                alt={attachment.name}
                className="h-full w-full object-cover"
              />
              {attachment.uploadStatus !== 'uploaded' ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 text-[10px] font-heading text-white">
                  <span>{attachment.uploadStatus === 'failed' ? 'Failed' : `${progress}%`}</span>
                  {attachment.uploadStatus === 'uploading' ? (
                    <div className="mt-1 h-1.5 w-10 overflow-hidden rounded-full bg-white/20">
                      <div
                        className="h-full bg-white transition-[width] duration-150"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
              <button
                type="button"
                className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white transition-colors hover:bg-black"
                onClick={() => onRemove(attachment.id)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )
        }

        return (
          <div
            key={attachment.id}
            className="inline-flex max-w-full items-center gap-2 rounded-base border-2 border-nb-border bg-bg px-2 py-1 text-xs"
          >
            {attachment.uploadStatus === 'failed' ? (
              <FileText className="h-3.5 w-3.5 shrink-0 text-destructive" />
            ) : (
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-foreground/60" />
            )}
            <span className="max-w-[180px] truncate">{attachment.name}</span>
            <span className="text-foreground/45">
              {attachment.uploadStatus === 'uploading'
                ? `${progress}%`
                : attachment.uploadStatus === 'failed'
                  ? 'Failed'
                  : 'Ready'}
            </span>
            <button
              type="button"
              className="rounded-base p-0.5 text-foreground/50 transition-colors hover:bg-secondary-bg hover:text-foreground"
              onClick={() => onRemove(attachment.id)}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
